import React, {useState, useLayoutEffect, useEffect, useRef} from "react";
import {graphql} from "gatsby";
import PropTypes from "prop-types";
import {css} from "@emotion/react";
import {camelCase, snakeCase} from "change-case";
import {breakpoints} from "~styles/global";
import Section from "~components/Section";
import RichText from "~components/RichText";
import Seo from "~components/Seo";
import BackButtonSection from "~components/sections/BackButtonSection";
import ImageSlider from "~components/blocks/ImageSliderEmbla";
import ProductAccordion from "~components/blocks/ProductAccordion";
import Download from "~components/blocks/Download";
import BrandTile from "~components/blocks/BrandTile";
import ProductFeature from "~components/blocks/ProductFeature";
import ProductEnquiryForm from "~components/blocks/ProductEnquiryForm";
import ProductGridSection from "~components/sections/ProductGridSection";
import CTASection from "~components/sections/CTASection";
import VariantAccordionItem from "~components/blocks/VariantAccordionItem";
import Button from "~components/blocks/Button";
import WishlistButton from "~components/blocks/WishlistButton";
import resolveLink from "~utils/resolveLink";
import Modal from "~components/Modal";
import SwatchModal from "~components/blocks/SwatchModal";
import {useCart, useSiteState} from "~context/siteContext";
import useSiteSettings from "~utils/useSiteSettings";
import JumpLinks from "../../components/blocks/JumpLinks";
import {paramCase} from "change-case";
import {useQueryParams, StringParam} from "use-query-params";
import ProductDocument from "~components/ProductDocument";
import {PDFDownloadLink} from "@react-pdf/renderer";
import {DateTime} from "luxon";
import toPlainText from "~utils/toPlainText";
import {Link} from "gatsby";

const Product = ({data, pageContext}) => {
  const {mobile, tablet} = breakpoints;
  const {title, content, options, purchasable, slug, _id, _type} = data.sanityProduct;
  const isPreview = process.env.NODE_ENV === "development" || process.env.GATSBY_PREVIEW;

  const [isMobile, setIsMobile] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [validOptions, setValidOptions] = useState({});
  const [selectedVariant, setSelectedVariant] = useState({});
  const [triggerSlideChange, setTriggerSlideChange] = useState(null);
  const [selectedSwatch, setSelectedSwatch] = useState(null);
  const [adding, setAdding] = useState(null);
  const [stockLimit, setStockLimit] = useState(null);
  const [activeLink, setActiveLink] = useState(null);
  const [sliderInit, setSliderInit] = useState(false);

  const [urlParams, setUrlParams] = useQueryParams({
    variant: StringParam,
  });

  const {addItem} = useCart();
  const [siteState] = useSiteState();
  const {wholesaleDiscount} = useSiteSettings();

  const numberWithCommas = (x) => {
    if (!x) return null;
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    window.setTimeout(() => {
      setIsClient(true);
    }, 600);
  }, []);

  useLayoutEffect(() => {
    setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", (e) => {
      setIsMobile(window.innerWidth <= 768);
    });
    return window.removeEventListener("resize", (e) => {
      setIsMobile(window.innerWidth <= 768);
    });
  }, []);

  // Set initial options
  useLayoutEffect(() => {
    let initialVariantIndex = 0;
    if (urlParams.variant) {
      const index = content?.variants.findIndex(
        (v) => v.content?.shopifyVariantID === urlParams.variant,
      );
      if (index > -1) {
        initialVariantIndex = index;
      }
    }
    if (purchasable && content?.variants) {
      let v = content?.variants[initialVariantIndex].content;
      let filteredV = {};
      Object.keys(options).forEach((key) => {
        const optionName = options[key].name;
        let optionValue = v[optionName];
        optionValue = optionValue ? optionValue.toLowerCase() : optionValue;
        filteredV = {
          ...filteredV,
          [optionName]: optionValue,
        };
      });
      setSelectedOptions(filteredV);
    }
  }, []);

  const findMatchingVariant = (options) => {
    const variants = content?.variants?.map((v) => v.content);
    const matchingVariants = variants?.filter((v) => {
      let hasMatch = true;
      Object.keys(options).forEach((key) => {
        let optionValue = v[key] ? v[key].toLowerCase() : v[key];
        if (optionValue !== options[key]) {
          hasMatch = false;
        }
      });
      return hasMatch;
    });
    return matchingVariants;
  };

  const validateOptions = () => {
    const allOptions = options;
    let valid = {};

    allOptions?.forEach((option) => {
      option.values.forEach((value) => {
        let optionName = camelCase(option.name);

        const potentialOptions = {
          ...selectedOptions,
          [optionName]: value,
        };
        const matchingVariant = findMatchingVariant(potentialOptions);
        const isAvailable = matchingVariant?.length > 0;
        if (isAvailable) {
          valid = {
            ...valid,
            [optionName]: [...(valid[optionName] ?? []), value],
          };
        }
      });
    });
    return valid;
  };

  // Update Select Variant and Valid Options when selections change, this also sets the inital variant to variants[0]

  useEffect(() => {
    const valid = validateOptions();
    setValidOptions(valid);

    if (selectedOptions && purchasable) {
      // Find a variant that matches these options
      const match = findMatchingVariant(selectedOptions);
      if (match) {
        setSelectedVariant(match[0]);
      }

      if (match && match[0]) {
        // Move the image slider to the corrent point, according to the new selected variant
        const imageIds =
          content?.images?.map((entry) =>
            entry.relatedVariants?.map((v) => v.content?.shopifyVariantID),
          ) ?? [];
        const slideIndex = imageIds.findIndex((entry) => {
          return entry?.includes(match[0].shopifyVariantID);
        });

        if (slideIndex >= 0) {
          if (!sliderInit) {
            window.setTimeout(() => {
              setTriggerSlideChange(slideIndex);
              setSliderInit(true);
            }, 300);
          } else {
            setTriggerSlideChange(slideIndex);
          }
        }
      } else {
        setTriggerSlideChange(null);
      }
    }
  }, [selectedOptions]);

  const applyFinish = (optionValue) => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
    setSelectedSwatch(null);
    const [option, value] = optionValue.split(":");
    setSelectedOptions({...selectedOptions, [option]: value});
  };

  const enquiryFormRef = useRef();

  const scrollToEnquiry = () => {
    enquiryFormRef.current.scrollIntoView({
      behavior: "smooth",
    });
  };

  let variantObject;

  if (selectedVariant) {
    variantObject = {
      variantId: selectedVariant.shopifyVariantID ?? undefined,
      title: title,
      productSlug: slug,
      productId: _id,
      qty: 1,
      maxQty: selectedVariant.stock,
      price: selectedVariant?.price,
      brand: content?.brand?.title,
      options: selectedOptions,
      image: selectedVariant.image ?? content?.cardImage,
    };

    // strip out all extra unneccessary image data to reduce the size of the object
    let palette = variantObject?.image?.asset?.metadata?.palette;
    let lqip = variantObject?.image?.asset?.metadata?.lqip;

    if (palette) {
      variantObject.image.asset.metadata.palette = null;
    }
    if (lqip) {
      variantObject.image.asset.metadata.lqip = null;
    }
  }

  const add = () => {
    const added = addItem(variantObject);
    if (added) {
      setAdding(true);
    } else {
      setStockLimit(true);
    }
    window.setTimeout(() => {
      setAdding(false);
      setStockLimit(false);
    }, 1000);
  };

  const cartCount =
    siteState.cart.find((c) => c.variantId === selectedVariant?.shopifyVariantID)?.qty || 0;

  const allFinishes = !purchasable
    ? null
    : content?.finishes
        ?.map((finish) => finish?.sets)
        ?.flat()
        ?.map((set) => set?.swatches)
        ?.flat()
        ?.map((entry) => ({
          relatedOption: entry?.relatedOption,
          image: entry?.swatch?.image,
        }));

  // console.log({selectedVariant})
  return (
    <>
      <Seo
        title={content?.seo?.metaTitle ?? title}
        image={content?.seo?.socialImage?.asset?.url}
        description={
          content?.seo?.metaDescription ??
          (content?.information ? toPlainText(content?.information) : null)
        }
        url={"/" + pageContext.canonical}
      />
      {/* Product Header */}

      <BackButtonSection message={`back`} customBackLink={`/products`} />

      {/* Action Buttons Desktop */}
      <div>
        <div
          css={css`
            position: sticky;
            top: 100vh;
            margin-left: 20px;
            transform: translateY(calc(-100% - 50px));
            z-index: 30;
            width: fit-content;
            padding: 20px;
            background-color: var(--beige);
            border-radius: 10px;
            pointer-events: none;
            ${mobile} {
              display: none;
            }
            button {
              display: inline-grid;
            }
          `}
        >
          <Button
            onClick={() => scrollToEnquiry()}
            css={css`
              grid-column: ${purchasable ? "span 2" : "span 1"};
              pointer-events: all;
            `}
          >
            Enquire
          </Button>
          <WishlistButton
            productObject={variantObject}
            css={css`
              display: inline-block;
              margin-left: 22px;
              transform: translateY(5px);
              opacity: ${selectedVariant ? 1 : 0.5};
              pointer-events: ${!selectedVariant ? "none" : "all"};
            `}
          />
          {purchasable && selectedVariant && selectedVariant?.stock > 0 && (
            <>
              <br />
              <Button
                onClick={() =>
                  selectedVariant ? add() : window.scrollTo({top: 0, behavior: "smooth"})
                }
                css={css`
                  margin-top: 20px;
                  min-width: 78px;
                  opacity: ${selectedVariant ? 1 : 0.5};
                  pointer-events: ${adding ? "none" : "all"};
                  span {
                    &:after {
                      opacity: ${adding ? "0" : "1"};
                    }
                  }
                `}
              >
                {adding ? "Added!" : "Add to bag"}
              </Button>
              <br />
            </>
          )}
          {purchasable && (
            <Link to="/shipping-returns-and-refunds">
              <Button
                onClick={() => null}
                css={css`
                  /* margin-left: 20px; */
                  grid-column: ${purchasable ? "span 2" : "span 1"};
                  margin-top: 20px;
                  pointer-events: all;
                `}
              >
                Shipping, Returns & Refunds
              </Button>
            </Link>
          )}
        </div>
        <Section
          css={css`
            > div {
              grid-template-rows: max-content max-content 1fr;
            }
          `}
        >
          <h1
            css={css`
              grid-column: span 12;
              padding: 12px 0 22px;
              ${mobile} {
                padding-bottom: 24px;
              }
            `}
          >
            {title} by {content?.brand?.title}
          </h1>
          {/* Product Details */}
          <div
            css={css`
              grid-column: 1 / 4;
              display: grid;
              grid-template-columns: 1fr 2fr;
              grid-column-gap: 20px;
              align-self: start;
              padding-top: ${purchasable ? "0px" : "45px"};
              ${mobile} {
                grid-column: span 10;
                grid-template-columns: 3fr 7fr;
                margin-bottom: 60px;
              }
            `}
          >
            {content?.brand?.title && (
              <>
                <h6>Brand</h6>
                <h6>{content?.brand?.title}</h6>
              </>
            )}
            {content?.designer && (
              <>
                <h6>Designer</h6>
                <h6>{content?.designer}</h6>
              </>
            )}
            {content?.origin && (
              <>
                <h6>Origin</h6>
                <h6>{content?.origin}</h6>
              </>
            )}
            {content?.materials && (
              <>
                <h6>Materials</h6>
                <h6>{content?.materials?.map((material) => material.title).join(", ")}</h6>
              </>
            )}
            {(content?.priceFrom || selectedVariant?.price) && (
              <>
                <h6>RRP</h6>
                {selectedVariant?.price ? (
                  <h6>${numberWithCommas(selectedVariant?.price)}</h6>
                ) : (
                  <h6>From ${numberWithCommas(content?.priceFrom)}</h6>
                )}
              </>
            )}
            {/* {(content?.priceFrom || selectedVariant.price) && siteState.wholesale &&
            <>
            <h6>Wholesale</h6>
            {selectedVariant?.price ? (
              <h6>${numberWithCommas(Math.round(selectedVariant.price * ((100 - wholesaleDiscount) / 100)))}</h6>
            ) : (
              <h6>From ${numberWithCommas(Math.round(content?.priceFrom * ((100 - wholesaleDiscount) / 100)))}</h6>
            )}
            </>
          } */}
            {purchasable && (
              <div
                className="h6"
                css={css`
                  grid-column: span 2;
                  margin-top: 3em;
                  /* br{
								display: none;
							} */
                `}
              >
                <RichText content={content?.information} />
              </div>
            )}
          </div>
          <ImageSlider
            slides={content?.images}
            height={isMobile ? "60vw" : "35vw"}
            slideChange={triggerSlideChange}
            css={css`
              grid-column: 5 / 13;
              margin-bottom: 120px;
              grid-row: span 2;
              ${mobile} {
                grid-column: span 12;
                margin-bottom: 60px;
              }
            `}
          />

          {/* Variant Selector */}

          {purchasable && (
            <div
              css={css`
                grid-column: span 3;
                margin-top: 67px;
                margin-bottom: 40px;
                ${tablet} {
                  grid-column: span 4;
                }
                ${mobile} {
                  grid-column: span 12;
                  margin-top: 0;
                  margin-bottom: 30px;
                }
              `}
            >
              {options?.map((option) => (
                <VariantAccordionItem
                  title={option.name}
                  values={option.values}
                  allFinishes={allFinishes}
                  initialValue={selectedOptions[option.name] ?? null}
                  validValues={validOptions[camelCase(option.name)]}
                  onChange={(val) =>
                    setSelectedOptions({
                      ...selectedOptions,
                      [camelCase(option.name)]: val,
                    })
                  }
                  key={option._key}
                />
              ))}
              {purchasable && (
                <div
                  css={css`
                    margin-top: 30px;
                    margin-bottom: 30px;
                    ${mobile} {
                      margin-top: 24px;
                      margin-bottom: 24px;
                    }
                  `}
                >
                  <button
                    onClick={() => add()}
                    disabled={selectedVariant?.stock === 0 || cartCount >= selectedVariant?.stock}
                    className="h4"
                    css={css`
                      background-color: ${adding ? "var(--dark-grey)" : "black"};
                      color: white;
                      padding: 9px 10px;
                      pointer-events: all;
                      min-width: 240px;
                      display: inline-block;
                      transition:
                        background-color 0.3s,
                        color 0.3s;
                      &:focus,
                      &:active {
                        color: white;
                      }
                      &:hover {
                        background-color: var(--dark-grey);
                      }
                      &:disabled {
                        cursor: not-allowed;
                        background-color: var(--dark-grey);
                      }
                    `}
                  >
                    {adding ? "Added!" : "Add to bag"}
                  </button>
                </div>
              )}
              <p
                css={css`
                  margin-top: 20px;
                `}
              >
                {!selectedVariant || selectedVariant?.stock < 0 ? "Not" : selectedVariant?.stock} in
                stock
              </p>
            </div>
          )}
        </Section>
        <Section>
          {/* Action Buttons Mobile */}
          <div
            css={css`
              grid-column: span 12;
              margin-bottom: 88px;
              display: none;
              top: 0px;
              position: sticky;
              padding: 16px 0 20px;
              background-color: var(--beige);
              z-index: 20;
              button {
                display: inline-grid;
              }
              ${mobile} {
                display: block;
              }
            `}
          >
            <Button
              onClick={() => scrollToEnquiry()}
              css={css`
                margin-right: 19px;
                display: inline-block;
                background-color: var(--beige);
              `}
            >
              Enquire
            </Button>
            <WishlistButton
              css={css`
                display: inline-block;
                transform: translateY(5px);
                ${mobile} {
                  svg {
                    width: 19px;
                  }
                }
              `}
            />
            {purchasable && (
              <>
                {purchasable && selectedVariant && selectedVariant?.stock > 1 && (
                  <>
                    <br />
                    <Button
                      onClick={() =>
                        selectedVariant && selectedVariant?.stock > 0
                          ? add()
                          : window.scrollTo({top: 0, behavior: "smooth"})
                      }
                      css={css`
                        min-width: 72px;
                        opacity: ${selectedVariant ? 1 : 0.5};
                        pointer-events: ${adding ? "none" : "all"};
                        margin-right: 16px;
                        background-color: var(--beige);
                        margin-top: 20px;
                        span {
                          &:after {
                            opacity: ${adding ? "0" : "1"};
                          }
                        }
                      `}
                    >
                      {adding ? "Added!" : "Add to bag"}
                    </Button>
                  </>
                )}
                <br />
                <Link to="/shipping-returns-and-refunds">
                  <Button
                    onClick={() => null}
                    css={css`
                      margin-top: 20px;
                      grid-column: ${purchasable ? "span 2" : "span 1"};
                      pointer-events: all;
                    `}
                  >
                    Shipping, Returns & Refunds
                  </Button>
                </Link>
              </>
            )}
          </div>
          {/* Jump Links */}
          <div
            css={css`
              position: sticky;
              top: 60px;
              white-space: nowrap;
              left: 40px;
              height: 0;
              width: 0;
              letter-spacing: 0.1em;
              padding-bottom: ${purchasable ? `280px` : `260px`};
              ${mobile} {
                display: block;
                position: relative;
                top: 0;
                left: 0;
                height: auto;
                width: auto;
                padding-bottom: 80px;
              }
            `}
          >
            <div
              css={css`
                position: absolute;
                top: 0px;
                ${mobile} {
                  position: relative;
                }
              `}
            >
              <JumpLinks
                oneColumn
                links={[
                  ...(content?.information
                    ? [
                        {
                          title: `Information`,
                          id: `information`,
                        },
                      ]
                    : []),
                  ...(content?.dimensions
                    ? [
                        {
                          title: `Dimensions`,
                          id: `dimensions`,
                        },
                      ]
                    : []),
                  ...(content?.finishes
                    ? [
                        {
                          title: `Finishes`,
                          id: `finishes`,
                        },
                      ]
                    : []),
                  ...(siteState.wholesale || isPreview
                    ? [
                        {
                          title: `Downloads`,
                          id: `downloads`,
                        },
                      ]
                    : []),
                  {
                    title: `Brand`,
                    id: `${content?.brand?.title ? paramCase(content?.brand?.title) : null}`,
                  },
                  ...(content?.features
                    ? [
                        {
                          title: `Features`,
                          id: `features`,
                        },
                      ]
                    : []),
                ]}
                onUpdate={(active) => setActiveLink(active)}
                currentLink={activeLink}
              ></JumpLinks>
            </div>
          </div>
          <ProductAccordion
            css={css`
              grid-column: 5 / 13;
              max-width: 790px;
              margin-bottom: 18px;
              ${mobile} {
                grid-column: span 12;
              }
            `}
            purchasable={purchasable}
            onSwatchClick={(swatch) => setSelectedSwatch(swatch)}
            productDetails={{
              information: {
                title: "Information",
                text: content?.information,
              },
              dimensionsText: content?.dimensionsText,
              dimensions: {
                title: "Dimensions",
                dimensionBlocks: content?.dimensions,
              },
              finishesText: content?.finishesText,
              finishes: {
                finishOptions: content?.finishes,
              },
              technicalInformationText: content?.technicalInformationText,
              technicalInformation: {
                title: "Technical Information",
                technicalInformationBlocks: content?.technicalInformation,
              },
            }}
          />
          {selectedSwatch && (
            <Modal isShowing={true}>
              <SwatchModal
                finishes={content?.finishes}
                selected={selectedSwatch}
                slideToShow={selectedSwatch.swatchIndex}
                purchasable={purchasable}
                close={() => setSelectedSwatch(null)}
                onApply={(value) => applyFinish(value)}
              />
            </Modal>
          )}

          {/* Downloads */}
          <div
            id="downloads"
            css={css`
              grid-column: 5 / 13;
              margin-bottom: 180px;
              ${mobile} {
                grid-column: span 12;
                display: grid;
                grid-template-columns: 1fr 1fr;
                margin-bottom: 148px;
              }
              h3 {
                margin-bottom: 40px;
              }
            `}
          >
            <h3>Downloads</h3>
            <div>
              {isClient && !content?.technicalSheet && (
                <PDFDownloadLink
                  document={<ProductDocument product={data.sanityProduct} />}
                  fileName={
                    snakeCase(
                      `Misura ${title} Technical Sheet ${DateTime.now().toFormat("dd-MM-yy")}`,
                    ) + ".pdf"
                  }
                >
                  {({blob, url, loading, error}) => (
                    <Download
                      name={loading ? "Loading..." : "Technical Sheet"}
                      inline={isMobile ? false : true}
                    />
                  )}
                </PDFDownloadLink>
              )}
              {content?.technicalSheet && (
                <Download
                  file={content?.technicalSheet}
                  key="tech-sheet"
                  name="Technical Sheet"
                  inline={isMobile ? false : true}
                />
              )}
              {(siteState.wholesale || isPreview) &&
                content?.downloads?.map((download) => (
                  <Download
                    file={download.file}
                    key={download.key}
                    name={download.name}
                    inline={isMobile ? false : true}
                  />
                ))}
            </div>
          </div>

          {content?.brand && (
            <BrandTile
              css={css`
                grid-column: 5 / 12;
                margin-bottom: 158px;
                ${mobile} {
                  grid-column: span 12;
                  margin-bottom: 150px;
                }
              `}
              id={content?.brand?.title ? paramCase(content?.brand?.title) : null}
              title={content?.brand?.title}
              shortDesc={content?.brand?.content?.shortText}
              image={content?.brand?.content?.image}
              catalogueLink={resolveLink("brandFilter", content?.brand?.title)}
              brandLink={resolveLink(content?.brand?._type, content?.brand?.slug?.current)}
            />
          )}

          {/* Features */}

          {content?.features && (
            <div
              id="features"
              css={css`
                grid-column: 5 / 11;
                h2 {
                  margin-bottom: 45px;
                }
                ${mobile} {
                  grid-column: span 12;
                  h2 {
                    display: none;
                  }
                }
              `}
            >
              <h2>Features</h2>
              {content?.features.map((feature) => (
                <ProductFeature
                  key={feature._key}
                  title={feature.title}
                  image={feature.image}
                  text={feature.text}
                  css={css`
                    margin-bottom: 254px;
                    ${mobile} {
                      margin-bottom: 140px;
                    }
                  `}
                />
              ))}
            </div>
          )}
          <div
            ref={enquiryFormRef}
            css={css`
              grid-column: 5 / 11;
              margin-bottom: 212px;
              ${mobile} {
                grid-column: span 12;
                margin-bottom: 140px;
              }
            `}
          >
            <ProductEnquiryForm
              title="Enquire about this product"
              productName={title}
              dropdownBackground="var(--beige)"
            />
          </div>
        </Section>
        <div
          css={css`
            height: 58px;
            margin-bottom: -58px;
          `}
        />
      </div>
      {/* Related Products */}

      <ProductGridSection
        products={content?.relatedProducts}
        centered={!isMobile}
        heading="Related products"
        storeLinkHeading="Go to store"
        storeLink="/products"
        css={css`
          margin-bottom: 200px;
          ${mobile} {
            margin-bottom: 152px;
          }
        `}
      />
      <CTASection
        title={content?.cta?.title}
        image={content?.cta?.image}
        text={content?.cta?.text}
        link={
          content?.cta?.link?.to
            ? resolveLink(content?.cta?.link?.to?._type, content?.cta?.link?.to?.slug?.current)
            : undefined
        }
        buttonText={content?.cta?.link?.linkText}
      />
      <div
        css={css`
          height: 106px;
          margin-bottom: -106px;
        `}
      />
    </>
  );
};

export const query = graphql`
  query productQuery($slug: String) {
    sanityProduct(slug: {current: {eq: $slug}}) {
      _type
      id
      _id
      title
      purchasable
      active
      slug {
        current
      }
      content: _rawContent(resolveReferences: {maxDepth: 10})
      options: _rawOptions(resolveReferences: {maxDepth: 10})
      # The query below here is to solve a bug in the preview which doesn't refresh references unless manually queried
      contentManual: content {
        brand {
          content {
            image {
              asset {
                _id
              }
            }
          }
        }
        finishes {
          sets {
            ... on SanityFinishSet {
              _key
              _type
              swatches {
                swatch {
                  image {
                    asset {
                      _id
                    }
                  }
                }
              }
            }
            ... on SanitySwatchSet {
              id
              name
              swatches {
                image {
                  asset {
                    id
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

Product.propTypes = {};

Product.defaultProps = {};

export default Product;
